import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

function strength(pw) {
  const checks = [
    pw.length >= 8, /[A-Z]/.test(pw), /[a-z]/.test(pw),
    /\d/.test(pw), /[^A-Za-z0-9]/.test(pw),
  ];
  return { passed: checks.filter(Boolean).length, checks };
}

export default function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [cp, setCp] = useState("");
  const [busy, setBusy] = useState(false);
  const s = useMemo(() => strength(np), [np]);

  const submit = async () => {
    if (!cur) { toast.error("Enter current password"); return; }
    if (s.passed < 5) { toast.error("Password doesn't meet all requirements"); return; }
    if (np !== cp) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: np });
      toast.success("Password updated");
      setCur(""); setNp(""); setCp("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded p-8 mt-6 space-y-4">
      <div>
        <div className="overline text-xs text-slate-500">SECURITY</div>
        <h3 className="font-editorial text-2xl mt-1">Change Password</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Fld label="Current Password" testid="cp-current" value={cur} onChange={setCur} />
        <Fld label="New Password" testid="cp-new" value={np} onChange={setNp} />
        <Fld label="Confirm New Password" testid="cp-confirm" value={cp} onChange={setCp} />
      </div>
      {np && (
        <>
          <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
            <div className={`h-full ${s.passed >= 5 ? "bg-emerald-500" : s.passed >= 3 ? "bg-amber-400" : "bg-red-400"}`}
                 style={{ width: `${(s.passed / 5) * 100}%` }} />
          </div>
          <ul className="text-[11px] grid grid-cols-2 md:grid-cols-5 gap-x-3">
            {["8+ chars","Uppercase","Lowercase","Number","Symbol"].map((l,i)=>(
              <li key={l} className={s.checks[i] ? "text-emerald-700" : "text-slate-400"}>
                {s.checks[i] ? "✓" : "·"} {l}
              </li>
            ))}
          </ul>
        </>
      )}
      <button data-testid="cp-submit" onClick={submit}
              disabled={busy || !cur || s.passed < 5 || np !== cp}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded text-sm font-medium disabled:bg-slate-300 inline-flex items-center gap-2">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
        {busy ? "Updating…" : "Update Password"}
      </button>
    </div>
  );
}

function Fld({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <input data-testid={testid} type="password" value={value} onChange={(e) => onChange(e.target.value)}
             className="w-full border border-border rounded px-3 py-2 text-sm" />
    </div>
  );
}
